import React, { useState } from 'react';
import AIChatBox from '../../components/AIChatBox';
import {
  CheckCircle,
  X,
  MessageSquare
} from 'lucide-react';

const Modal = ({ show, onClose, children }) => {
  if (!show) return null;
  
  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <div className="bg-slate-900 rounded-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center p-6 border-b border-slate-800">
          <h2 className="text-2xl font-semibold text-white">Chat with an Expert</h2>
          <button onClick={onClose} className="text-slate-400 hover:text-white">
            <X className="w-6 h-6" />
          </button>
        </div>
        <div className="p-6">
          {children}
        </div>
      </div>
    </div>
  );
};

const ChatModal = ({ show, onClose, initialMessage }) => {
  if (!show) return null;
  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <div className="bg-slate-900 rounded-xl max-w-4xl w-full">
        <div className="flex justify-between items-center p-6 border-b border-slate-800">
          <h2 className="text-2xl font-semibold text-white">Chat with Nia</h2>
          <button onClick={onClose} className="text-slate-400 hover:text-white">
            <X className="w-6 h-6" />
          </button>
        </div>
        <div className="p-6">
          <AIChatBox initialMessageModal={initialMessage} />
        </div>
      </div>
    </div>
  );
};

const PoCDevelopment = () => {
  const [showModal, setShowModal] = useState(false);
  const [showChatModal, setShowChatModal] = useState(false);
  const [chatMessage, setChatMessage] = useState('');

  const handleScheduleConsultant = (topic) => {
    const message = `Hi Nia, I'm interested in for the ${topic}. Could you please schedule a meeting with an expert?`;
    setShowChatModal(true);
    setChatMessage(message);
  };

  const handleChatWithNia = (topic) => {
    const message = `Hi Nia, I'm interested in ${topic}. Could you help me understand the details and potential implementation?`;
    setShowChatModal(true);
    setChatMessage(message);
  };

  const keyDeliverables = [
    "Technical design documents",
    "Sample code",
    "CI/CD pipeline",
    "GCP project in customer's account",
    "Infrastructure as code"
  ];

  const gCloudsTeam = [
    "Platform Engineer",
    "Project Manager", 
    "Professional Cloud Architect"
  ];

  return (
    <div className="min-h-screen bg-slate-950 text-white">
      <div className="max-w-5xl mx-auto px-4 py-16">
        {/* Hero Section */}
        <div className="mb-16">
          <h1 className="text-4xl font-bold text-white mb-4">
            Proof of Concept Development
          </h1>
          <p className="text-xl text-slate-400 mb-6">
            Transform innovative ideas into actionable prototypes. We build targeted use cases aligned with your business objectives, creating robust prototypes that validate technical feasibility and business potential.
          </p>
          <div className="space-x-4">
            <button 
              onClick={() => handleScheduleConsultant('Proof of Concept Development')}
              className="bg-blue-500 text-white px-6 py-3 rounded-lg hover:bg-blue-600 transition-colors"
            >
              Consult with an expert
            </button>
            {/*<button 
              className="text-blue-400 border border-blue-500 px-6 py-3 rounded-lg hover:bg-blue-500/10 transition-colors"
            >
              Explore consulting services
            </button>
            */}
          </div>
        </div>

        {/* What Your Team Gets */}
        <div className="bg-slate-900/50 rounded-xl p-8 mb-16 backdrop-blur-xl border border-slate-800">
          <h2 className="text-2xl font-semibold text-white mb-6">What we deliver</h2>
          
          <div className="space-y-6">
            <div className="flex items-start space-x-4">
              <CheckCircle className="w-6 h-6 text-blue-400 mt-1 flex-shrink-0" />
              <div>
                <h3 className="font-semibold text-white">Use Case Development</h3>
                <p className="text-slate-400">Build targeted use cases that align precisely with your business objectives, technical infrastructure, and strategic goals.</p>
              </div>
            </div>

            <div className="flex items-start space-x-4">
              <CheckCircle className="w-6 h-6 text-blue-400 mt-1 flex-shrink-0" />
              <div>
                <h3 className="font-semibold text-white">Comprehensive Prototyping</h3>
                <p className="text-slate-400">Develop a production-candidate prototype including infrastructure as code, prototype coding, and CI/CD pipeline integration.</p>
              </div>
            </div>

            <div className="flex items-start space-x-4">
              <CheckCircle className="w-6 h-6 text-blue-400 mt-1 flex-shrink-0" />
              <div>
                <h3 className="font-semibold text-white">Strategic Validation</h3>
                <p className="text-slate-400">Provide insights that enable business teams to evaluate innovative ideas with minimal initial investment.</p>
              </div>
            </div>
          </div>
        </div>

        {/* Deliverables and Team */}
        <div className="grid md:grid-cols-2 gap-8 mb-16">
          {/* Key Deliverables */}
          <div className="border border-slate-800 rounded-xl p-6 bg-slate-900/50 backdrop-blur-xl">
            <h3 className="text-xl font-semibold text-white mb-4">Key deliverables</h3>
            <ul className="space-y-2">
              {keyDeliverables.map((deliverable, index) => (
                <li key={index} className="flex items-center space-x-2">
                  <CheckCircle className="w-4 h-4 text-blue-400" />
                  <span className="text-slate-300">{deliverable}</span>
                </li>
              ))}
            </ul>
          </div>

          {/* Team Composition */}
          <div className="border border-slate-800 rounded-xl p-6 bg-slate-900/50 backdrop-blur-xl">
            <h3 className="text-xl font-semibold text-white mb-4">Team composition</h3>
            <ul className="space-y-2">
              {gCloudsTeam.map((role, index) => (
                <li key={index} className="flex items-center space-x-2">
                  <CheckCircle className="w-4 h-4 text-blue-400" />
                  <span className="text-slate-300">{role}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* Additional Details */}
        <div className="grid md:grid-cols-2 gap-8 mb-16">
          <div className="border border-slate-800 rounded-xl p-6 bg-slate-900/50 backdrop-blur-xl">
            <h3 className="text-xl font-semibold text-white mb-4">Duration</h3>
            <p className="text-slate-300">2-4 weeks (dependent on use case complexity)</p>
          </div>
          <div className="border border-slate-800 rounded-xl p-6 bg-slate-900/50 backdrop-blur-xl">
            <h3 className="text-xl font-semibold text-white mb-4">Pricing</h3>
            <p className="text-slate-300">Starting from £9K (variable based on project scope)</p>
          </div>
        </div>

        {/* Bottom CTA */}
        <div className="text-center bg-blue-500/10 rounded-xl p-8 border border-blue-500/50">
          <h2 className="text-2xl font-semibold text-white mb-4">
            Ready to Validate Your Innovative Ideas?
          </h2>
          <div className="space-x-4">
            <button 
              onClick={() => handleScheduleConsultant('Proof of Concept Development')}
              className="bg-blue-500 text-white px-8 py-3 rounded-lg hover:bg-blue-600 transition-colors"
            >
              Consult with an expert
            </button>
            <button
              onClick={() => handleChatWithNia('Proof of Concept Development')}
              className="inline-flex items-center bg-blue-500/10 text-blue-400 px-8 py-3 rounded-lg hover:bg-blue-500/20 transition-colors"
            >
              <MessageSquare className="mr-2 w-5 h-5" />
              Chat with Nia
            </button>
          </div>
        </div>
      </div>

      <Modal show={showModal} onClose={() => setShowModal(false)}>
        <div className="text-white">Expert Consultation Form Placeholder</div>
      </Modal>

      <ChatModal 
        show={showChatModal}
        onClose={() => setShowChatModal(false)}
        initialMessage={chatMessage}
      />
    </div>
  );
};

export default PoCDevelopment;